import React from 'react'
import './scheduling-helper-2024.css'
import axios from 'axios'
import { baseUrl } from '../../assets/Animations'
import { getRequestHeaders } from '../../api'
import { addMonths, eachDayOfInterval, endOfMonth, format, parse, startOfMonth, subMonths } from 'date-fns'
import { createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime } from '../../Calendar/calendarFunctions'
import { MdClose, MdDriveEta, MdHome, MdLockClock, MdPunchClock, MdSchedule, MdTimeline, MdWatch, MdWatchOff } from 'react-icons/md'

const WEEKDAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday']

const SchedulingHelper2024 = ({schedulingHelperTeacherId, teacherDistanceSuggestions, setSchedulingHelperTeacherId}) => {
    const [teacherInfo, setTeacherInfo] = React.useState({})
    const [calendarEvents, setCalendarEvents] = React.useState([])
    const [calendarDaysWithEvents, setCalendarDaysWithEvents] = React.useState(false)
    const [scrollCalendar, setScrollCalendar] = React.useState(false)
    const [nylasAvailability, setNylasAvailability] = React.useState({ byDay: {} })
    const [nylasAvailabilityLoading, setNylasAvailabilityLoading] = React.useState(false)
    const [nylasAvailabilityError, setNylasAvailabilityError] = React.useState('')

    React.useEffect(() => {
        if(!schedulingHelperTeacherId) return
        axios.get(`${baseUrl}/teacher-availability/${schedulingHelperTeacherId}`)
        .then(res => {
            setTeacherInfo(res.data)
        })
        .catch(err => console.log(err))
        axios.get(`${baseUrl}/calendar/teacher/${schedulingHelperTeacherId}`)
        .then(res => {
            setCalendarEvents(res.data)
        })
        .catch(err => console.log(err))
    }, [schedulingHelperTeacherId])

    const generateScrollCalendar = async (calendarEvents) => {
        const allDates = eachDayOfInterval({start: new Date(), end: addMonths(new Date(), 2)});
        createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime(allDates, calendarEvents, setCalendarDaysWithEvents);
    }

    const createEllipsis = (text, length) => {
        if(!text) return
        return text.length > length ? text.substring(0, length) + '...' : text;
    }

    const createEmptyDateMap = (dates = []) => {
        const map = {}
        dates.forEach((date) => {
            map[format(date, 'yyyy-MM-dd')] = []
        })
        return map
    }

    const isNumericId = (value) => {
        if (value == null) return false
        return String(value).trim() !== '' && !Number.isNaN(Number(value))
    }

    const toUnixSeconds = (value) => {
        if (value == null) return null
        if (typeof value === 'number') {
            return value > 1e12 ? Math.round(value / 1000) : value
        }
        const date = value instanceof Date ? value : new Date(value)
        if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
            return null
        }
        return Math.round(date.getTime() / 1000)
    }

    const normalizeSlot = (slot) => {
        if (!slot) return null
        const startUnix = toUnixSeconds(
            slot.start ??
            slot.start_time ??
            slot.startTime ??
            slot.begin ??
            slot.startDate ??
            slot.start_date
        )
        const endUnix = toUnixSeconds(
            slot.end ??
            slot.end_time ??
            slot.endTime ??
            slot.finish ??
            slot.endDate ??
            slot.end_date
        )
        if (startUnix == null || endUnix == null || endUnix <= startUnix) return null
        return { startUnix, endUnix }
    }

    const normalizeSlots = (rawSlots) => {
        if (!Array.isArray(rawSlots)) return []
        return rawSlots
            .map((slot) => normalizeSlot(slot))
            .filter(Boolean)
            .sort((a, b) => a.startUnix - b.startUnix)
    }

    const mapSlotsToDayMinutes = (slots, rangeDays = []) => {
        const map = createEmptyDateMap(rangeDays)
        const allowedDateKeys = new Set(Object.keys(map))
        slots.forEach((slot) => {
            const startDate = new Date(slot.startUnix * 1000)
            const endDate = new Date(slot.endUnix * 1000)
            if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return
            const dateKey = format(startDate, 'yyyy-MM-dd')
            if (!allowedDateKeys.has(dateKey)) return
            const startMinutes = startDate.getHours() * 60 + startDate.getMinutes()
            const endMinutes = endDate.getHours() * 60 + endDate.getMinutes()
            if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes) || endMinutes <= startMinutes) return
            map[dateKey].push({ start: startMinutes, end: endMinutes })
        })
        Object.keys(map).forEach((key) => {
            map[key].sort((a, b) => a.start - b.start)
        })
        return map
    }

    const minutesToDateOnDay = (dayDate, minutesValue) => {
        const base = new Date(dayDate)
        base.setHours(0, 0, 0, 0)
        return new Date(base.getTime() + (Number(minutesValue) || 0) * 60000)
    }

    const normalizeCalendarEvents = React.useCallback((events) => {
        if (!Array.isArray(events)) return [];
        return events
            .map((event) => {
                const dateTime =
                    event.dateTime ||
                    event.dateTimeUtc ||
                    event.DateTime ||
                    event.start?.dateTime ||
                    event.start?.date ||
                    event.startTime ||
                    event.date;
                if (!dateTime) return null;
                const endDateTime =
                    event.endDateTime ||
                    event.end?.dateTime ||
                    event.end?.date ||
                    event.endTime ||
                    event.end;
                let lessonLength = event.lessonLength;
                if ((!lessonLength || !Number.isFinite(Number(lessonLength))) && endDateTime) {
                    const startDate = new Date(dateTime);
                    const endDate = new Date(endDateTime);
                    if (!isNaN(startDate) && !isNaN(endDate)) {
                        lessonLength = Math.max(15, Math.round((endDate - startDate) / 60000));
                    }
                }
                return {
                    ...event,
                    dateTime,
                    endDateTime: endDateTime || event.endDateTime,
                    lessonLength: lessonLength || event.lessonLength || 30,
                    recurring: event.recurring || { isOn: false },
                    recurringEndDate: event.recurringEndDate || "",
                };
            })
            .filter(Boolean);
    }, []);

    const normalizedCalendarEvents = React.useMemo(
        () => normalizeCalendarEvents(calendarEvents),
        [calendarEvents, normalizeCalendarEvents]
    );

    const calendarRangeDays = React.useMemo(
        () => eachDayOfInterval({ start: new Date(), end: addMonths(new Date(), 2) }),
        []
    )

    const baseAvailabilityByDay = React.useMemo(() => {
        const details = teacherInfo?.AvailabilityDetails || {}
        const normalized = {}
        WEEKDAYS.forEach((day) => {
            const slots = Array.isArray(details[day]) ? details[day] : []
            normalized[day] = slots
                .map((slot) => {
                    if (!slot?.start || !slot?.end) return null
                    const [startH, startM] = String(slot.start).split(':').map((val) => Number(val))
                    const [endH, endM] = String(slot.end).split(':').map((val) => Number(val))
                    const start = startH * 60 + (startM || 0)
                    const end = endH * 60 + (endM || 0)
                    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null
                    return { start, end }
                })
                .filter(Boolean)
        })
        return normalized
    }, [teacherInfo])

    React.useEffect(() => {
        const teacherRecordId =
            teacherInfo?._id ||
            teacherInfo?.id ||
            teacherInfo?.teacherId ||
            teacherInfo?.TeacherId ||
            null
        const teacherLegacyId =
            teacherInfo?.TeacherID ||
            teacherInfo?.teacherID ||
            teacherInfo?.legacyTeacherId ||
            (isNumericId(schedulingHelperTeacherId) ? schedulingHelperTeacherId : null) ||
            null
        const primaryIdentifier = teacherLegacyId || teacherRecordId
        if (!primaryIdentifier) {
            setNylasAvailability({ byDay: createEmptyDateMap(calendarRangeDays) })
            setNylasAvailabilityError('')
            return
        }
        let cancelled = false
        setNylasAvailabilityLoading(true)
        setNylasAvailabilityError('')
        const start = new Date()
        start.setHours(0, 0, 0, 0)
        const end = addMonths(new Date(), 2)
        end.setHours(23, 59, 59, 999)
        const parsedTeacherId = Number(primaryIdentifier)
        const payload = {
            teacherId: Number.isFinite(parsedTeacherId) ? parsedTeacherId : primaryIdentifier,
            start: start.toISOString(),
            end: end.toISOString(),
            durationMinutes: 30,
            intervalMinutes: 15
        }
        if (teacherRecordId && String(teacherRecordId) !== String(payload.teacherId)) {
            payload.teacherRecordId = teacherRecordId
        }
        if (teacherLegacyId) {
            payload.teacherLegacyId = teacherLegacyId
        }
        const teacherEmail =
            teacherInfo.CalendarEmail ||
            teacherInfo.calendarEmail ||
            teacherInfo.calendarConnectionEmail ||
            teacherInfo.PersonalEmail ||
            teacherInfo.personalEmail ||
            teacherInfo.Email ||
            teacherInfo.email ||
            null
        if (teacherEmail) payload.email = teacherEmail

        axios
            .post(`${baseUrl}/api/integrations/nylas/availability`, payload, getRequestHeaders())
            .then((response) => {
                if (cancelled) return
                const rawSlots = response?.data?.timeSlots || response?.data?.time_slots || []
                const normalizedSlots = normalizeSlots(rawSlots)
                const availableByDate = mapSlotsToDayMinutes(normalizedSlots, calendarRangeDays)
                const busyByDate = createEmptyDateMap(calendarRangeDays)
                calendarRangeDays.forEach((date) => {
                    const dateKey = format(date, 'yyyy-MM-dd')
                    const dayName = format(date, 'EEEE')
                    const baseSlots = baseAvailabilityByDay[dayName] || [{ start: 0, end: 24 * 60 }]
                    const availableSlots = availableByDate[dateKey] || []
                    baseSlots.forEach((window) => {
                        let cursor = window.start
                        const overlaps = availableSlots
                            .map((slot) => ({
                                start: Math.max(slot.start, window.start),
                                end: Math.min(slot.end, window.end)
                            }))
                            .filter((slot) => slot.end > slot.start)
                            .sort((a, b) => a.start - b.start)
                        overlaps.forEach((slot) => {
                            if (slot.start > cursor) {
                                busyByDate[dateKey].push({ start: cursor, end: slot.start })
                            }
                            cursor = Math.max(cursor, slot.end)
                        })
                        if (cursor < window.end) {
                            busyByDate[dateKey].push({ start: cursor, end: window.end })
                        }
                    })
                })
                setNylasAvailability({ byDay: busyByDate })
            })
            .catch((error) => {
                if (cancelled) return
                const status = error?.response?.status
                const message =
                    error?.response?.data?.error ||
                    (status === 404
                        ? 'No connected personal calendar.'
                        : 'Unable to load Google Calendar availability.')
                setNylasAvailability({ byDay: createEmptyDateMap(calendarRangeDays) })
                setNylasAvailabilityError(message)
            })
            .finally(() => {
                if (!cancelled) setNylasAvailabilityLoading(false)
            })

        return () => {
            cancelled = true
        }
    }, [baseAvailabilityByDay, calendarRangeDays, schedulingHelperTeacherId, teacherInfo])

    React.useEffect(() => {
        if(teacherInfo.Name) {
            setScrollCalendar(getDatesWithEvents(calendarDaysWithEvents, normalizedCalendarEvents, nylasAvailability.byDay))
        }
    }, [calendarDaysWithEvents, normalizedCalendarEvents, nylasAvailability, teacherInfo])

    React.useEffect(() => {
        generateScrollCalendar(normalizedCalendarEvents)
    }, [normalizedCalendarEvents])

    const isPersonalEvent = (event) => {
        if (!event) return false
        if (event.isPersonal || event.isPersonalEvent) return true
        const type = String(event.type || event.eventType || event.category || '').toLowerCase()
        if (type.includes('personal') || type.includes('busy')) return true
        if (!event.studentName && !event.familyName && (event.title || event.summary || event.name)) return true
        return false
    }

    const getEventKey = (event, index) => {
        const base =
            event?._id ||
            event?.id ||
            event?.calendarEventId ||
            event?.dateTime ||
            event?.start?.dateTime ||
            event?.start?.date ||
            event?.title ||
            event?.summary ||
            event?.studentName ||
            'event'
        const start =
            event?.dateTime ||
            event?.startTime ||
            event?.start?.dateTime ||
            event?.start?.date ||
            ''
        const end =
            event?.endDateTime ||
            event?.endTime ||
            event?.end?.dateTime ||
            event?.end?.date ||
            ''
        return `${base}-${start}-${end}-${index}`
    }

    const hasCalendarSync = React.useMemo(() => {
        if (!teacherInfo) return false
        if (calendarEvents && calendarEvents.length > 0) return true
        if (nylasAvailability?.byDay) {
            const hasSlots = Object.values(nylasAvailability.byDay).some(
                (slots) => Array.isArray(slots) && slots.length > 0
            )
            if (hasSlots) return true
        }
        const candidates = [
            teacherInfo.calendarEmail,
            teacherInfo.calendarConnectionEmail,
            teacherInfo.CalendarID,
            teacherInfo.CalendarId,
            teacherInfo.calendarId,
            teacherInfo.calendarID,
            teacherInfo.calendarConnected,
            teacherInfo.CalendarConnected,
            teacherInfo.nylasEmail,
            teacherInfo.nylasConnectionEmail,
            teacherInfo.nylasConnected,
            teacherInfo.calendarSync?.connected,
            teacherInfo.nylasStatus?.connected,
        ]
        return candidates.some((val) => {
            if (typeof val === 'boolean') return val
            return Boolean(val)
        })
    }, [calendarEvents, nylasAvailability, teacherInfo])

    const getEventTitle = (event) => {
        return event.title || event.summary || event.name || event.subject || 'Personal Event'
    }

    const formatStudentName = (studentName, familyName) => {
        if(!studentName) return;
        // Convert both names to lowercase to make the comparison case-insensitive
        const lowerStudentName = studentName.toLowerCase();
        const lowerFamilyName = familyName.toLowerCase();
        
        // Only remove familyName if it's part of the studentName, but not if studentName equals familyName
        if (lowerStudentName !== lowerFamilyName && lowerStudentName.includes(lowerFamilyName)) {
          const regex = new RegExp(`\\b${familyName}\\b`, 'i'); // Use regex to remove the family name as a word
          studentName = studentName.replace(regex, '').trim(); // Remove familyName and trim extra spaces
        }
      
        // Return the formatted name
        return `${studentName} ${familyName}`.trim();
      }



const formatTeacherAvailabilityIntoCalendarEvent = (availability) => {
    if (!availability || !Array.isArray(availability)) return [];

    return availability
        .filter(slot => slot.start && slot.end) // Ensure valid start & end times
        .map(slot => {
            let parsedStart, parsedEnd;

            try {
                parsedStart = parse(slot.start, 'HH:mm', new Date());
                parsedEnd = parse(slot.end, 'HH:mm', new Date());

                return {
                    studentName: 'AVAILABLE',
                    familyName: `${format(parsedStart, 'h:mm a')} - ${format(parsedEnd, 'h:mm a')}`
                };
            } catch (error) {
                console.error(`Invalid time format for: ${slot.start} - ${slot.end}`, error);
                return null; // Skip invalid entries
            }
        })
        .filter(Boolean); // Remove any null values
};

const getDatesWithEvents = (data, rawEvents, busyByDate) => {
    const result = {};
    if (!data) return result;

    Object.entries(data).forEach(([date, value]) => {
        const dayOfWeek = format(parse(date, 'yyyy-MM-dd', new Date()), 'EEEE');

        const teacherAvailability = teacherInfo?.AvailabilityDetails?.[dayOfWeek];
        if (teacherAvailability && teacherAvailability.length > 0) {
            if (!result[date]) result[date] = [];

            // Get **all** available slots, not just the first
            const availableSlots = formatTeacherAvailabilityIntoCalendarEvent(teacherAvailability);
            result[date].push(...availableSlots);
        }

        if (value.events.length > 0) {
            if (!result[date]) result[date] = [];
            const normalizedEvents = value.events
                .map((event) => {
                    const dateTime =
                        event.dateTime ||
                        event.dateTimeUtc ||
                        event.DateTime ||
                        event.start?.dateTime ||
                        event.start?.date ||
                        event.date
                    return dateTime ? { ...event, dateTime } : null
                })
                .filter(Boolean)
            result[date].push(...normalizedEvents);
        }
    });

    if (Array.isArray(rawEvents) && rawEvents.length) {
        const startRange = new Date();
        const endRange = addMonths(new Date(), 2);
        rawEvents.forEach((event, idx) => {
            const startDateTime =
                event.dateTime ||
                event.dateTimeUtc ||
                event.DateTime ||
                event.start?.dateTime ||
                event.start?.date ||
                event.startTime ||
                event.date;
            const endDateTime =
                event.endDateTime ||
                event.end?.dateTime ||
                event.end?.date ||
                event.endTime ||
                event.end;
            if (!startDateTime && !endDateTime) return;
            const parsedStart = startDateTime ? new Date(startDateTime) : null;
            const parsedEnd = endDateTime ? new Date(endDateTime) : null;
            const startOk = parsedStart && !isNaN(parsedStart) ? parsedStart : null;
            const endOk = parsedEnd && !isNaN(parsedEnd) ? parsedEnd : null;
            if (!startOk && !endOk) return;
            const inRange =
                (startOk && startOk >= startRange && startOk <= endRange) ||
                (endOk && endOk >= startRange && endOk <= endRange);
            if (!inRange) return;
            const anchor = startOk || endOk;
            const dateKey = format(anchor, 'yyyy-MM-dd');
            if (!result[dateKey]) result[dateKey] = [];
            const eventId = event._id || event.id || event.calendarEventId || `evt-${idx}-${dateKey}`;
            const existing = result[dateKey].some(
                (e) =>
                    (e._id || e.id || e.calendarEventId || '') === eventId &&
                    String(e.dateTime || e.date || '') === String(startDateTime || endDateTime)
            );
            if (existing) return;
            result[dateKey].push({
                ...event,
                _id: eventId,
                dateTime: startDateTime || endDateTime,
            });
        });
    }

    if (busyByDate) {
        Object.entries(busyByDate).forEach(([dateKey, slots]) => {
            if (!Array.isArray(slots) || !slots.length) return
            if (!result[dateKey]) result[dateKey] = []
            slots.forEach((slot, idx) => {
                if (slot.end <= slot.start) return
                const startDate = minutesToDateOnDay(parse(dateKey, 'yyyy-MM-dd', new Date()), slot.start)
                const endDate = minutesToDateOnDay(parse(dateKey, 'yyyy-MM-dd', new Date()), slot.end)
                result[dateKey].push({
                    _id: `personal-${dateKey}-${idx}-${slot.start}-${slot.end}`,
                    studentName: null,
                    familyName: null,
                    title: 'Personal Event',
                    isPersonalEvent: true,
                    dateTime: startDate.toISOString(),
                    endDateTime: endDate.toISOString(),
                    lessonLength: Math.max(15, slot.end - slot.start)
                })
            })
        })
    }

    // Sort events by time
    for (const date in result) {
        result[date].sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));
    }

    return result;
};


    //   const formatTeacherAvailabilityIntoCalendarEvent = (availability) => {
    //     for(let i = 0; i < availability.length; i++) {
    //         if(availability[i].start && availability[i].end) {
    //             return {studentName: 'AVAILABLE', familyName: `${format(parse(availability[i].start, 'H:mm', new Date()), 'h:mm a')} - ${format(parse(availability[i].end, 'H:mm', new Date()), 'h:mm a')}`}
    //         }
    //     }
    //   }

    //   const getDatesWithEvents = (data) => {
    //     // Create a new object to store the dates with events
    //     const result = {};
      
    //     // Loop through the data object using Object.entries
    //     Object.entries(data).forEach(([date, value]) => {
    //       // Check if the events array is not empty
    //       if(teacherInfo.AvailabilityDetails[format(parse(date, 'yyyy-MM-dd', new Date()), 'EEEE')].length > 0) {
    //         if(!result[date]) result[date] = []
    //         result[date].push(formatTeacherAvailabilityIntoCalendarEvent(teacherInfo.AvailabilityDetails[format(parse(date, 'yyyy-MM-dd', new Date()), 'EEEE')]))
    //     } 
    //      if (value.events.length > 0) {
    //         if(!result[date]) result[date] = []
    //         for(let i = 0; i < value.events.length; i++) {
    //             result[date].push(value.events[i])
    //         }
    //       }
    //     });
        
    //     //sort the events by time
    //     for (const date in result) {
    //         result[date].sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));
    //     }

    //     return result;
    //   }

      const createEventEndTime = (event) => {
        //add lessonLength to the start time
        let time = new Date(event.dateTime)
        let endTime = time.setMinutes(time.getMinutes() + event.lessonLength)
        //return the formatted time
        return format(new Date(endTime), 'h:mm a')
      }

      const createPersonalEventEndTime = (event) => {
        const end =
            event.endDateTime ||
            event.end?.dateTime ||
            event.endTime ||
            event.end
        if (end) return format(new Date(end), 'h:mm a')
        if (event.lessonLength) return createEventEndTime(event)
        const time = new Date(event.dateTime)
        return format(new Date(time.setMinutes(time.getMinutes() + 30)), 'h:mm a')
      }

      const findDriveTime = (event, distanceSuggestions) => {
        //find the teacherDistanceSuggestions object that matches the teacherId
        let familyId = event.familyId
        let teacherDistance = distanceSuggestions.find(sugg => sugg.eventOrTeacherObject.familyId === familyId)
        //return the drive time
        if(teacherDistance) {
            return teacherDistance.duration
        } else {
            return 'Unavailable'
        }
      }

      const findDriveTimeFromTeachersHome = (event, distanceSuggestions) => {
        let teacherDistance = distanceSuggestions.find(sugg => (sugg.eventOrTeacherObject.type === 'teacher') && (sugg.eventOrTeacherObject.name === teacherInfo.Name))
        //return the drive time
        if(teacherDistance) {
            return teacherDistance.duration
        } else {
            return 'No drive time available'
        }
      }

    return (
        teacherInfo?.Name ?

        <div className='scheduling-helper-column'>
            <div className="scheduling-helper-column__top"></div>
            <div className='scheduling-helper-column__header-wrapper'>
                <div className='scheduling-helper-column__teacher-name'>{teacherInfo?.Name}</div>
                <div className='scheduling-helper-column__closer' onClick={() => setSchedulingHelperTeacherId(false)}><MdClose size={24} color='#94A3B8' /></div>
            </div>
            <div className={`scheduling-helper-column__calendar-sync ${hasCalendarSync ? 'is-synced' : 'is-off'}`}>
                <span
                    className={`scheduling-helper-column__calendar-sync-dot ${hasCalendarSync ? 'is-synced' : 'is-off'}`}
                    aria-hidden='true'
                />
                <div>{hasCalendarSync ? 'Personal calendar synced' : 'No connected personal calendar'}</div>
            </div>
            {teacherInfo.AdminNotes !== '' && 
                <div>
                    <div>
                        <div className='scheduling-helper-column__note-label'>Admin Notes:</div>
                        <div className='scheduling-helper-column__notes'>{teacherInfo.AdminNotes}</div>
                    </div>
                    <div className='scheduling-helper-column__dividing-line'></div>
                </div>
            }
            {teacherInfo.Notes !== '' &&
                <div>
                    <div className='scheduling-helper-column__note-label'>Teacher Notes:</div>
                    <div className='scheduling-helper-column__notes'>{teacherInfo.Notes}</div>
                </div>
            }
            <div>       
            </div>

                    {scrollCalendar &&
                        <div>
                                <div className='scheduling-helper-column__main-scroll'>
                                    {
                                        Object.entries(scrollCalendar).map(([date, events]) => (
                                            <div key={date} className='scheduling-helper-column__each-date'>
                                                <div className='scheduling-helper-column__date' >
                                                    <div className='scheduling-helper-column__date-day'>{format(parse(date, 'yyyy-MM-dd', new Date()), 'E').toUpperCase()}</div>
                                                    <div className='scheduling-helper-column__date-month'>{format(parse(date, 'yyyy-MM-dd', new Date()), 'MMM').toUpperCase()}</div>
                                                    <div className='scheduling-helper-column__date-number'>{format(parse(date, 'yyyy-MM-dd', new Date()), 'd')}</div>
                                                    
                                                </div>
                                                <div className='scheduling-helper-column__each-event__wrapper'>
                                                    {events.map((event, index) => {
                                                        const eventKey = getEventKey(event, index)
                                                        return (
                                                        event.studentName === 'AVAILABLE' ?
                                                        <div key={eventKey} className='scheduling-helper-column__each-event__availability'>
                                                            <div className='scheduling-helper-column__each-event__availability__title'>
                                                                <div>{event.studentName}</div>
                                                                <div className='scheduling-helper-column__each-event__availability__drive-time__wrapper'>
                                                                    <MdHome color='#94A3B8' size={10} />
                                                                    <div>{findDriveTimeFromTeachersHome(event, teacherDistanceSuggestions)}</div>
                                                                </div>
                                                            </div>
                                                            
                                                            <div className='scheduling-helper-column__each-event__availability__time-wrapper'>
                                                                <MdSchedule size={14} fill='#4BA2E3' />
                                                                <div className='scheduling-helper-column__each-event__availability__time'>{event.familyName}</div>
                                                            </div>
                                                            
                                                            
                                                        </div>
                                                        :
                                                        isPersonalEvent(event) ?
                                                        <div key={eventKey} className='scheduling-helper-column__each-event scheduling-helper-column__each-event--personal'>
                                                            <div className='scheduling-helper-column__each-event__availability__title'>
                                                               <div>{createEllipsis(getEventTitle(event), 18)}</div>
                                                            </div>
                                                            <div className='scheduling-helper-column__each-event__availability__time-wrapper'>
                                                                <MdLockClock size={14} fill='#e2e8f0' />
                                                                <div className='scheduling-helper-column__each-event__availability__time'>
                                                                    {format(new Date(event.dateTime), 'h:mm a')} - {createPersonalEventEndTime(event)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        :
                                                        <div key={eventKey} className='scheduling-helper-column__each-event'>
                                                            <div className='scheduling-helper-column__each-event__availability__title'>
                                                               <div>{createEllipsis(formatStudentName(event.studentName, event.familyName), 13)}</div>
                                                               <div className='scheduling-helper-column__each-event__availability__drive-time__wrapper'>
                                                                    <MdDriveEta color='#94A3B8' size={10} />
                                                                    <div>{findDriveTime(event, teacherDistanceSuggestions)}</div>
                                                               </div>
                                                            </div>
                                                            <div className='scheduling-helper-column__each-event__availability__time-wrapper'>
                                                                <MdSchedule size={14} fill='#4BA2E3' />
                                                                <div className='scheduling-helper-column__each-event__availability__time'>{format(new Date(event.dateTime), 'h:mm a')} - {createEventEndTime(event)}</div>
                                                            </div>
                                                        </div>
                                                    )})}
                                                </div>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                    }
        </div>

            :

                <div>Loading...</div>
    )
}
export default SchedulingHelper2024;
